import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DashboardShell } from "@/components/Shell";
import { QrCode, Download, Loader2 } from "lucide-react";

export default function AdminQR() {
  const [locations, setLocations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState("locations");
  const [selected, setSelected] = useState(null); // {type, id}
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data));
    api.get("/staff").then((r) => setStaff(r.data));
  }, []);

  async function showQR(type, id) {
    setSelected({ type, id });
    setLoading(true);
    setQr(null);
    const baseUrl = window.location.origin;
    try {
      const path = type === "location" ? `/qr/location/${id}` : `/qr/staff/${id}`;
      const { data } = await api.get(path, { params: { base_url: baseUrl } });
      setQr(data);
    } finally {
      setLoading(false);
    }
  }

  function download() {
    if (!qr) return;
    const a = document.createElement("a");
    a.href = qr.image;
    a.download = `nod-qr-${selected.type}-${selected.id}.png`;
    a.click();
  }

  const list = tab === "locations" ? locations : staff;

  return (
    <DashboardShell role="admin">
      <div className="mb-8">
        <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Verwaltung</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mt-2 flex items-center gap-3"><QrCode className="w-9 h-9 text-[#EBB300]" /> QR-Codes</h1>
        <p className="text-[#A1A1AA] mt-2">Zwei Arten: Location-QR (zeigt das Service-Team) und Mitarbeiter-QR (direkt zum Profil).</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="bg-[#121216] border border-white/5 rounded-full p-1 flex gap-1 mb-4 max-w-sm" data-testid="admin-qr-tabs">
            <button onClick={() => { setTab("locations"); setSelected(null); setQr(null); }} data-testid="admin-qr-tab-location"
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${tab === "locations" ? "bg-[#EBB300] text-[#0A0A0C]" : "text-[#A1A1AA]"}`}>
              Location
            </button>
            <button onClick={() => { setTab("staff"); setSelected(null); setQr(null); }} data-testid="admin-qr-tab-staff"
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${tab === "staff" ? "bg-[#EBB300] text-[#0A0A0C]" : "text-[#A1A1AA]"}`}>
              Mitarbeiter
            </button>
          </div>

          <div className="space-y-2" data-testid="admin-qr-list">
            {list.map((item) => (
              <button key={item.id} onClick={() => showQR(tab === "locations" ? "location" : "staff", item.id)}
                data-testid={`admin-qr-item-${item.id}`}
                className={`w-full text-left p-4 rounded-2xl border transition-colors flex items-center gap-3 ${
                  selected?.id === item.id ? "border-[#EBB300] bg-[#EBB300]/5" : "border-white/5 bg-[#121216] hover:border-white/20"
                }`}>
                {item.photo_url && <img src={item.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{item.name}</div>
                  <div className="text-xs text-[#A1A1AA] truncate">{item.address || item.bio}</div>
                </div>
                <QrCode className="w-4 h-4 text-[#A1A1AA]" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="sticky top-6 bg-[#121216] border border-white/5 rounded-2xl p-6 text-center">
            {!selected && !qr && (
              <div className="py-20 text-[#52525B]">
                <QrCode className="w-12 h-12 mx-auto mb-4" strokeWidth={1.2} />
                <div>Wähle einen Eintrag, um den QR-Code anzuzeigen.</div>
              </div>
            )}
            {loading && <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#EBB300]" /></div>}
            {qr && (
              <>
                <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-4">
                  {qr.location?.name || qr.staff?.name}
                </div>
                <img src={qr.image} alt="QR" data-testid="admin-qr-preview-image" className="w-64 h-64 mx-auto rounded-2xl border border-white/10" />
                <div className="text-[10px] text-[#52525B] mt-3 break-all">{qr.url}</div>
                <button onClick={download} data-testid="admin-qr-download"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold text-sm hover:bg-[#FAD02C]">
                  <Download className="w-4 h-4" /> PNG herunterladen
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
